/** Points per visible tick: the total climbs in tens, then lands on the exact score. */
export const SCORE_STEP_POINTS = 10;
/** Even cadence per tick; ease-out would make the last ticks dwell longest. */
export const SCORE_STEP_MS = 60;
/** Cap so a big word never counts past the next likely input. */
export const SCORE_COUNT_MAX_MS = 300;

/** Ticks needed to cover `delta`, including the final landing tick. */
function countSteps(delta: number): number {
  return Math.ceil(delta / SCORE_STEP_POINTS);
}

export function countDuration(delta: number): number {
  return Math.min(countSteps(delta) * SCORE_STEP_MS, SCORE_COUNT_MAX_MS);
}

/** Climbs by whole ticks so every tick dwells equally; the final frame always equals `to`. */
export function countUpValue(from: number, to: number, elapsedMs: number): number {
  if (elapsedMs <= 0) return from;
  const delta = to - from;
  const durationMs = countDuration(delta);
  if (elapsedMs >= durationMs) return to;
  const ticks = Math.floor(countSteps(delta) * (elapsedMs / durationMs));
  return from + Math.min(ticks * SCORE_STEP_POINTS, delta);
}

interface ScoreTallyOptions {
  /** Reduced-motion (OS or the in-app Motion opt-in) jumps straight to the target. */
  motionAllowed?: () => boolean;
  now?: () => number;
  requestFrame?: (callback: () => void) => number;
  cancelFrame?: (id: number) => void;
}

/**
 * Animates `displayed` toward each new tentative score when the gain spans more than
 * one tick. Retargeting mid-flight restarts from the currently shown value; single-tick
 * gains, drops and resets jump instantly.
 */
export class ScoreTally {
  displayed = $state(0);
  private frame = 0;
  private readonly motionAllowed: () => boolean;
  private readonly now: () => number;
  private readonly requestFrame: (callback: () => void) => number;
  private readonly cancelFrame: (id: number) => void;

  constructor(options: ScoreTallyOptions = {}) {
    this.motionAllowed = options.motionAllowed ?? (() => true);
    this.now = options.now ?? (() => performance.now());
    this.requestFrame =
      options.requestFrame ?? ((callback) => window.requestAnimationFrame(() => callback()));
    this.cancelFrame = options.cancelFrame ?? ((id) => window.cancelAnimationFrame(id));
  }

  retarget(target: number): void {
    this.cancel();
    if (countSteps(target - this.displayed) <= 1 || !this.motionAllowed()) {
      this.displayed = target;
      return;
    }
    const from = this.displayed;
    const startedAt = this.now();
    const tick = () => {
      this.frame = 0;
      this.displayed = countUpValue(from, target, this.now() - startedAt);
      if (this.displayed !== target) {
        this.frame = this.requestFrame(tick);
      }
    };
    this.frame = this.requestFrame(tick);
  }

  reset(value = 0): void {
    this.cancel();
    this.displayed = value;
  }

  dispose(): void {
    this.cancel();
  }

  private cancel(): void {
    if (this.frame) {
      this.cancelFrame(this.frame);
      this.frame = 0;
    }
  }
}
